import { useState, useMemo, useEffect, useRef } from "react";
import { Eye, Plus, Trash2, Shield, AlertTriangle, Activity, TrendingUp, TrendingDown, Minus, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { useEvents } from "@/hooks/useConflictData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";

const DEFAULT_LOCATIONS: WatchLocation[] = [
  { name: "Kyiv", country: "Ukraine", lat: 50.45, lng: 30.52 },
  { name: "Gaza City", country: "Palestine", lat: 31.5, lng: 34.47 },
  { name: "Kharkiv", country: "Ukraine", lat: 49.99, lng: 36.23 },
];

interface WatchLocation {
  name: string;
  country: string;
  lat: number;
  lng: number;
}

const RISK_COLORS: Record<number, string> = {
  1: "text-success",
  2: "text-success",
  3: "text-warning",
  4: "text-destructive",
  5: "text-destructive",
};

const RISK_BG: Record<number, string> = {
  1: "bg-success/10 border-success/30",
  2: "bg-success/10 border-success/30",
  3: "bg-warning/10 border-warning/30",
  4: "bg-destructive/10 border-destructive/30",
  5: "bg-destructive/10 border-destructive/30",
};

const RISK_LABELS: Record<number, string> = {
  1: "Low",
  2: "Moderate",
  3: "Elevated",
  4: "High",
  5: "Critical",
};


function computeRiskIndex(events: any[], location: WatchLocation): number {
  const now = Date.now();
  const h72 = 72 * 60 * 60 * 1000;
  const radius = 1.5;

  const nearby = events.filter(e => {
    if (!e.lat || !e.lng || !e.occurred_at) return false;
    const dt = now - new Date(e.occurred_at).getTime();
    if (dt > h72) return false;
    return Math.abs(e.lat - location.lat) < radius && Math.abs(e.lng - location.lng) < radius;
  });

  if (nearby.length === 0) return 1;

  const weights: Record<string, number> = { missile: 3, air_raid: 2.5, drone: 2, explosion: 2, movement: 1 };
  let score = 0;
  for (const e of nearby) {
    const w = weights[e.event_type] || 1;
    const age = (now - new Date(e.occurred_at).getTime()) / (1000 * 60 * 60);
    const recency = age < 12 ? 1.5 : age < 24 ? 1.2 : 1;
    score += w * recency;
  }

  if (score >= 20) return 5;
  if (score >= 12) return 4;
  if (score >= 6) return 3;
  if (score >= 2) return 2;
  return 1;
}

function countEvents24h(events: any[], location: WatchLocation): number {
  const now = Date.now();
  const h24 = 24 * 60 * 60 * 1000;
  const radius = 1.5;
  return events.filter(e => {
    if (!e.lat || !e.lng || !e.occurred_at) return false;
    const dt = now - new Date(e.occurred_at).getTime();
    if (dt > h24) return false;
    return Math.abs(e.lat - location.lat) < radius && Math.abs(e.lng - location.lng) < radius;
  }).length;
}

const PremiumWatchlist = () => {
  const { isPremium, user } = useAuth();
  const { data: events } = useEvents(undefined, true);
  const [locations, setLocations] = useState<WatchLocation[]>([]);
  const [newName, setNewName] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const alertedRef = useRef<Set<string>>(new Set());

  // Load from DB
  useEffect(() => {
    if (!user || !isPremium) return;
    const load = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("watchlist_locations")
        .eq("user_id", user.id)
        .single();
      
      const saved = data?.watchlist_locations as unknown as WatchLocation[] | null;
      setLocations(saved && Array.isArray(saved) && saved.length > 0 ? saved : DEFAULT_LOCATIONS);
      setLoaded(true);
    };
    load();
  }, [user, isPremium]);

  // Persist to DB on change
  useEffect(() => {
    if (!user || !loaded) return;
    supabase
      .from("profiles")
      .update({ watchlist_locations: locations as any })
      .eq("user_id", user.id)
      .then();
  }, [locations, user, loaded]);

  const locationStats = useMemo(() => {
    if (!events) return [];
    return locations.map(loc => ({
      ...loc,
      count24h: countEvents24h(events, loc),
      risk: computeRiskIndex(events, loc),
    }));
  }, [events, locations]);

  // High risk toast alerts
  useEffect(() => {
    if (!isPremium || locationStats.length === 0) return;
    for (const loc of locationStats) {
      if (loc.risk >= 4 && !alertedRef.current.has(loc.name)) {
        alertedRef.current.add(loc.name);
        toast({
          variant: "destructive",
          title: `⚠️ ${RISK_LABELS[loc.risk]} Risk — ${loc.name}`,
          description: `Risk level ${loc.risk}/5 detected in ${loc.name}, ${loc.country}. ${loc.count24h} events in last 24h. Premium alert service.`,
        });
      }
    }
    // Reset alerts for locations that dropped below 4
    for (const name of alertedRef.current) {
      const stat = locationStats.find(l => l.name === name);
      if (stat && stat.risk < 4) alertedRef.current.delete(name);
    }
  }, [locationStats, isPremium]);

  if (!isPremium) return null;

  const addLocation = async () => {
    const name = newName.trim();
    if (!name || locations.length >= 5 || geocoding) return;
    if (locations.find(l => l.name.toLowerCase() === name.toLowerCase())) {
      toast({ title: "Location already in watchlist", variant: "destructive" });
      return;
    }

    setGeocoding(true);
    try {
      const { data, error } = await supabase.functions.invoke("geocode", {
        body: { query: name },
      });

      if (error || !data?.found) {
        toast({
          title: "Location not found",
          description: `Could not resolve "${name}". Try a different spelling.`,
          variant: "destructive",
        });
        return;
      }

      setLocations(prev => [...prev, {
        name: data.name,
        country: data.country,
        lat: data.lat,
        lng: data.lng,
      }]);
      setNewName("");
      setShowAdd(false);
    } catch {
      toast({ title: "Geocoding failed", description: "Please try again later.", variant: "destructive" });
    } finally {
      setGeocoding(false);
    }
  };

  const removeLocation = (name: string) => {
    setLocations(prev => prev.filter(l => l.name !== name));
  };

  const TrendIcon = ({ risk }: { risk: number }) => {
    if (risk >= 4) return <TrendingUp className="h-3 w-3" />;
    if (risk <= 2) return <TrendingDown className="h-3 w-3" />;
    return <Minus className="h-3 w-3" />;
  };

  return (
    <section id="watchlist" className="border-b border-border">
      <div className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <Eye className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-foreground">Your Watchlist</h2>
                <p className="text-[11px] sm:text-xs text-muted-foreground">
                  Monitor up to 5 locations • 24h event counts • 72h risk index
                </p>
              </div>
            </div>
            {locations.length < 5 && (
              <Button variant="outline" size="sm" className="gap-1.5 w-full sm:w-auto" onClick={() => setShowAdd(!showAdd)}>
                <Plus className="h-3.5 w-3.5" />
                Add Location
              </Button>
            )}
          </div>

          <AnimatePresence>
            {showAdd && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-4 overflow-hidden"
              >
                <div className="flex gap-2">
                  <Input
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    placeholder="Enter city name (e.g., Beirut, Odesa, Tel Aviv)..."
                    className="bg-secondary/50 text-sm"
                    onKeyDown={e => e.key === "Enter" && addLocation()}
                  />
                   <Button size="sm" onClick={addLocation} disabled={!newName.trim() || geocoding}>
                     {geocoding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add"}
                   </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {locationStats.map((loc, i) => (
              <motion.div
                key={loc.name}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Card className="group relative overflow-hidden transition-colors hover:border-primary/30">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-sm font-semibold">{loc.name}</CardTitle>
                        <p className="text-[11px] text-muted-foreground">{loc.country}</p>
                      </div>
                      <button
                        onClick={() => removeLocation(loc.name)}
                        className="rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 pt-0">
                    <div className="flex items-center gap-3">
                      <div className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 ${RISK_BG[loc.risk]}`}>
                        <Shield className={`h-3.5 w-3.5 ${RISK_COLORS[loc.risk]}`} />
                        <span className={`font-mono text-lg font-bold ${RISK_COLORS[loc.risk]}`}>{loc.risk}</span>
                        <div className="flex flex-col">
                          <span className={`text-[9px] font-semibold uppercase leading-none ${RISK_COLORS[loc.risk]}`}>
                            {RISK_LABELS[loc.risk]}
                          </span>
                          <span className="text-[8px] text-muted-foreground">72h risk</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Activity className="h-3.5 w-3.5 text-muted-foreground" />
                        <div className="flex flex-col">
                          <span className="font-mono text-sm font-bold text-foreground">{loc.count24h}</span>
                          <span className="text-[8px] text-muted-foreground">events 24h</span>
                        </div>
                      </div>
                      <div className={`ml-auto flex items-center gap-1 ${RISK_COLORS[loc.risk]}`}>
                        <TrendIcon risk={loc.risk} />
                      </div>
                    </div>
                    {loc.risk >= 4 && (
                      <div className="mt-2 flex items-center gap-1.5 rounded bg-destructive/10 px-2 py-1">
                        <AlertTriangle className="h-3 w-3 text-destructive" />
                        <span className="text-[10px] font-medium text-destructive">
                          Premium Alert: Elevated threat level detected
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {locationStats.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
              <Eye className="h-8 w-8 opacity-40" />
              <p className="text-sm">No locations in your watchlist. Add one to start monitoring.</p>
            </div>
          )}

          <p className="mt-4 text-center text-[10px] text-muted-foreground">
            Risk index is calculated from event frequency, type severity, and recency within ~150km radius over 72 hours. Premium members receive automatic alerts for risk level 4+.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PremiumWatchlist;
